import client from '@mc/networking/http';

// es-logger is imported by entry points on the illegal-flag-import list
// (router, page-data, bugsnag, wink, hosted-page, mc-ecs-storage), so we
// MUST NOT have a top-level import of @mc/flags. Read flag state from
// globalStore directly (the same backing data @mc/flags reads from).
// In tests, globalStore is not initialised, so we fall back to requiring
// @mc/flags which Jest can mock via mockFlags(). See perfUtils.js for the
// same pattern.
/* eslint-disable local-rules/no-invalid-flag-reference */
function isBatchAndBackoffFlagOn() {
  try {
    const flagKey = FLAGS.XP_ES_LOGGER_BATCH_AND_BACKOFF;
    if (typeof __TEST__ !== 'undefined' && __TEST__) {
      const { isOn } = require('@mc/flags');
      return isOn(flagKey);
    }
    const { usePageDataStore } = require('@mc/globalStore');
    if (usePageDataStore) {
      const flags = usePageDataStore.getState().flags;
      return Boolean(flags && flags[flagKey]);
    }
    return false;
  } catch {
    return false;
  }
}
/* eslint-enable local-rules/no-invalid-flag-reference */

/**
 * Describes log classifications as told by Intuit.
 * https://iis-compliance.a.intuit.com/SEC-200-Data-Classification-and-Handling-Policy.pdf
 */

export const CLASSIFICATION = {
  /**
   * Examples include: Cryptographic secrets, private keys, passwords, user credentials,
   * financial institution user credentials, security codes, access codes, PINs, and answers
   * to security questions.
   */
  SECRET: 'secret',

  /**
   * Examples include: Date of birth, government-issued ID numbers (e.g. driver’s license,
   * SSN, TIN, passport), financial account numbers (e.g. bank, credit, debit card numbers),
   * and Protected Health Information (PHI) such as medical record numbers, and financial
   * transaction data containing any other Highly Sensitive elements.
   */
  HIGHLY_SENSITIVE: 'highly_sensitive',

  /**
   * Examples include: Name(First, Middle, or Last Name), phone/fax number, Email,
   * username, street address, marital status, IP addresses, serial numbers, device IDs,
   * Intuit strategic or Merger and Acquisition information, credit score, Intuit vulnerability
   * information(this also correlates to what data the vulnerability might impact), and Intuit
   * non-public source code.
   */
  SENSITIVE: 'sensitive',

  /**
   * Examples include: Internal Intuit memos, organizational information, Intuit Policies and
   * Standards, production logs(not including Sensitive, Highly Sensitive Data, or Secret
   * Data), and non-identifiable product usage data.
   */
  RESTRICTED: 'restricted',

  /**
   * Examples include: Press releases, published financial reports, and approved marketing
   * material.
   */
  PUBLIC: 'public',
};

const CLASSIFICATION_ALLOW_LIST = Object.values(CLASSIFICATION);

const TRACK_LOG_URL = '/track/log';

// Batching + backoff parameters. Tuned for /track/log specifically:
// flush window short enough to keep logs useful, cooldown long enough
// to let WAF rate-limit windows expire. 250ms keeps the in-page-crash
// loss window small enough to preserve most error logs that fire just
// before an uncaught exception breaks the SPA.
const FLUSH_INTERVAL_MS = 250;
const MAX_QUEUE_SIZE = 50;
// sendBeacon caps payload at 64KB across browsers. Each entry can carry
// ~800 chars of data plus envelope (~1KB worst-case), so chunking at 25
// keeps each beacon comfortably under the limit.
const BEACON_CHUNK_SIZE = 25;
const COOLDOWN_BASE_MS = 30 * 1000;
const COOLDOWN_MAX_MS = 10 * 60 * 1000;

let queue = [];
let flushTimer = null;
let cooldownUntil = 0;
let consecutiveFailures = 0;
let unloadHandlersAttached = false;
let queueOverflowReported = false;
let queueOverflowDroppedCount = 0;

function buildSingleFormData({ channelName, message, data }) {
  const formData = new FormData();
  formData.append('channelName', channelName);
  formData.append('message', message);
  formData.append('data', JSON.stringify(data));
  return formData;
}

function buildBatchFormData(entries) {
  const formData = new FormData();
  formData.append(
    'entries',
    JSON.stringify(
      entries.map(({ channelName, message, data }) => ({
        channelName,
        message,
        data,
      })),
    ),
  );
  return formData;
}

function parseRetryAfterSeconds(response) {
  const header = response?.headers?.get?.('Retry-After');
  if (!header) {
    return null;
  }
  const seconds = Number.parseInt(header, 10);
  return Number.isFinite(seconds) && seconds > 0 ? seconds : null;
}

function enterCooldown(response) {
  consecutiveFailures += 1;
  const retryAfterSec = parseRetryAfterSeconds(response);
  const backoff = retryAfterSec
    ? retryAfterSec * 1000
    : Math.min(
        COOLDOWN_BASE_MS * 2 ** (consecutiveFailures - 1),
        COOLDOWN_MAX_MS,
      );
  cooldownUntil = Date.now() + backoff;
}

function resetCooldown() {
  consecutiveFailures = 0;
  cooldownUntil = 0;
}

function scheduleFlush() {
  if (flushTimer !== null) {
    return;
  }
  flushTimer = setTimeout(() => {
    flushTimer = null;
    flushQueue();
  }, FLUSH_INTERVAL_MS);
}

function flushQueue() {
  if (queue.length === 0) {
    return;
  }
  if (Date.now() < cooldownUntil) {
    // Drop the buffered entries; we don't want to retain them across a
    // long cooldown because /log is best-effort and queueing across
    // minutes pushes memory + risks bursting once cooldown expires.
    queue = [];
    return;
  }

  const entries = queue;
  queue = [];

  client
    .post(TRACK_LOG_URL, buildBatchFormData(entries))
    .then((response) => {
      // Treat anything that isn't an explicit `ok: true` Response as a
      // failure — a non-Response or response with `ok === undefined` would
      // otherwise wrongly reset cooldown.
      if (!response || !response.ok) {
        enterCooldown(response);
      } else {
        resetCooldown();
      }
    })
    .catch(() => {
      // Network-level rejection — treat like a non-2xx response so a
      // persistently-failing endpoint doesn't keep getting hammered.
      enterCooldown(null);
    });
}

// sendBeacon returns false if the payload is rejected (typically because
// it exceeds the browser's per-beacon cap). Retry by halving the chunk
// until each piece is accepted or we're sending a single entry.
function sendBeaconChunk(entries) {
  if (entries.length === 0) {
    return;
  }
  try {
    if (navigator.sendBeacon(TRACK_LOG_URL, buildBatchFormData(entries))) {
      return;
    }
  } catch {
    // sendBeacon can throw on some unload paths; fall through to split.
  }
  if (entries.length === 1) {
    // Nothing more we can do — drop and move on.
    return;
  }
  const mid = Math.ceil(entries.length / 2);
  sendBeaconChunk(entries.slice(0, mid));
  sendBeaconChunk(entries.slice(mid));
}

function flushQueueOnUnload() {
  if (queue.length === 0) {
    return;
  }
  if (Date.now() < cooldownUntil) {
    queue = [];
    return;
  }
  const entries = queue;
  queue = [];

  // sendBeacon is fire-and-forget and survives page unload. We can't
  // observe the response, so we don't update cooldown state from here.
  if (typeof navigator === 'undefined' || !navigator.sendBeacon) {
    return;
  }
  for (let i = 0; i < entries.length; i += BEACON_CHUNK_SIZE) {
    sendBeaconChunk(entries.slice(i, i + BEACON_CHUNK_SIZE));
  }
}

function attachUnloadHandlers() {
  if (unloadHandlersAttached || typeof window === 'undefined') {
    return;
  }
  unloadHandlersAttached = true;
  // These handlers are intentionally process-lifetime — they flush queued
  // logs as the page goes away and cannot be removed after the fact.
  // eslint-disable-next-line local-rules/remove-applied-listeners
  window.addEventListener('pagehide', flushQueueOnUnload);
  // eslint-disable-next-line local-rules/remove-applied-listeners
  window.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      flushQueueOnUnload();
    }
  });
}

function reportQueueOverflowOnce() {
  if (queueOverflowReported) {
    return;
  }
  queueOverflowReported = true;
  // Emit as a queue entry rather than a direct POST so the breadcrumb
  // rides the same batching/backoff path and doesn't pile on if the
  // server is already throttling us.
  queue.push({
    channelName: 'mc-errors-client',
    message: 'es-logger queue overflow',
    data: {
      dropped: queueOverflowDroppedCount,
      max_queue_size: MAX_QUEUE_SIZE,
    },
  });
}

function enqueue(entry) {
  attachUnloadHandlers();
  if (queue.length >= MAX_QUEUE_SIZE) {
    // Drop oldest to make room. Newer entries are usually more
    // actionable for debugging the current session.
    queue.shift();
    queueOverflowDroppedCount += 1;
    reportQueueOverflowOnce();
  }
  queue.push(entry);
  scheduleFlush();
}

/**
 * This function send messages to the `/track/log` endpoint as a means of logging
 * client side messages to ElasticSearch. While it returns a promise, it doesn't expect
 * the result to be awaited on. If there are any errors, it swallows them up in the
 * catch block.
 *
 * Note that channelName must be in CHANNEL_ALLOWLIST in app/controllers/TrackController.php,
 * otherwise the message will be dropped.
 *
 * @param {Object} logData parameters needed to send messages to ElasticSearch
 *
 * @param {string} logData.channelName name of the channel to send the message to
 * @param {string} logData.message message to send to the channel
 * @param {Object} logData.data data to send to the channel
 * @param {string} [logData.classification] classification of the data being logged
 *
 * @returns {Promise} response from the server
 */
export default function log({
  channelName,
  message,
  data = {},
  classification,
}) {
  if (typeof channelName !== 'string') {
    throw new Error('channelName must be provided');
  }

  if (typeof message !== 'string') {
    throw new Error('message must be provided');
  }

  if (classification) {
    if (!CLASSIFICATION_ALLOW_LIST.includes(classification)) {
      throw new Error(
        `${classification} is an unexpected logging classification`,
      );
    }

    // Append log classification in the format needed
    data = {
      log_classification: classification,
      ...data,
    };
  }

  if (isBatchAndBackoffFlagOn()) {
    if (Date.now() < cooldownUntil) {
      // Drop silently — the server has signalled we should slow down.
      return;
    }
    enqueue({ channelName, message, data });
    return;
  }

  client
    .post(TRACK_LOG_URL, buildSingleFormData({ channelName, message, data }))
    .catch(() => {});
}

// Test-only hook to reset module state between tests.
export const __resetForTests = () => {
  queue = [];
  if (flushTimer !== null) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
  cooldownUntil = 0;
  consecutiveFailures = 0;
  queueOverflowReported = false;
  queueOverflowDroppedCount = 0;
};
